import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Paper } from '@mui/material';
import { SurveyTemplateTranslateDto } from '@families-link/shared';
import { useBlocker, useParams } from 'react-router-dom';
import { useGetSurveyTemplateByIdQuery } from '../../../../store/api/survey.api';
import { getLocalStorageDistrictId } from '../../../_core/utils';
import { useSessionStorage } from '../../../../hooks';
import { EnglishSurveyTemplateForm } from '../../components/master-survey-forms/english-survey-form.component';
import { SurveySettingsForm } from '../../components/master-survey-forms/settings-survey-form';
import { TranslateSurveyTemplateForm } from '../../components/master-survey-forms/translate-survey-form.component';
import { skipToken } from '@reduxjs/toolkit/dist/query';
import { CoreLoadingWall } from '../../../_core/components/_ui/core-loading-wall';
import { useMuiModal } from '../../../_core/hooks';
import { ConfirmationModal, ConfirmationModalProps } from '../../../_core/components/confirmation-modal';
import { buttonModalWindowStyles } from '../../../_core/styles';
import { useTranslation } from 'react-i18next';

export type SurveyTemplateFormRef = {
	isDirty: boolean;
	isSubmitting: boolean;
	resetForm: () => void;
};

const mainSurveyInitiateValues = {
	questions: [],
	isFinished: true,
};
const initialTranslationsObject = {
	Template: mainSurveyInitiateValues,
};

export const SurveyTemplateActionPage: FC = () => {
	const isAdminRoute = window.location.pathname.startsWith('/admin');
	const [sessionStorageDistrictId] = useSessionStorage('districtId');
	const currentDistrictId = sessionStorageDistrictId ?? getLocalStorageDistrictId();
	const { surveyTemplateId } = useParams();
	const { data: surveyTemplate, refetch } = useGetSurveyTemplateByIdQuery(surveyTemplateId ?? skipToken);

	const [t] = useTranslation();

	useEffect(() => {
		if (surveyTemplateId) {
			refetch();
		}
	}, [refetch, surveyTemplateId]);

	const defaultTranslationsObject = useMemo(
		() =>
			surveyTemplate
				? surveyTemplate.translates?.reduce(
						(allObj: any, surveyTemplateTranslate: SurveyTemplateTranslateDto) => {
							allObj[surveyTemplateTranslate.language] = {
								...surveyTemplateTranslate,
								questions: surveyTemplate.questions?.map((question: any) => {
									const translatedQuestion = surveyTemplateTranslate.questions?.find(
										(translatedQuestion: any) => translatedQuestion.questionId === question._id
									);
									if (translatedQuestion) {
										const newOptions = question?.options?.map((option: any) => {
											const translatedOption = translatedQuestion?.options?.find(
												(translatedOption: any) =>
													translatedOption.optionId && translatedOption.optionId?.toString() === option._id?.toString()
											);
											if (translatedOption) {
												return {
													...option,
													_id: translatedOption._id,
													optionId: translatedOption.optionId,
													translatedText: translatedOption.translatedText,
												};
											}

											return {
												...option,
											};
										});

										return { ...question, ...translatedQuestion, options: newOptions };
									} else {
										return question;
									}
								}),
							};
							return allObj;
						},
						{ Template: { isFinished: true, questions: surveyTemplate.questions } }
				  )
				: initialTranslationsObject,
		[surveyTemplate]
	);

	const isDefaultTemplate = useMemo(() => {
		if (!surveyTemplate?.translates?.length) return false;

		return surveyTemplate.translates.some((t) => t.isDefault === true);
	}, [surveyTemplate]);

	const initialSettingsValues = useMemo(
		() =>
			surveyTemplate
				? {
						_id: surveyTemplate._id,
						districtIds: surveyTemplate.districtIds,
						title: surveyTemplate.title,
						adminTemplate: surveyTemplate.adminTemplate,
						isDefaultTemplate: isDefaultTemplate,
				  }
				: {
						districtIds: currentDistrictId && !isAdminRoute ? [currentDistrictId] : [],
						title: '',
						adminTemplate: isAdminRoute,
						isDefaultTemplate: false,
				  },
		[surveyTemplate, defaultTranslationsObject]
	);

	const [currentLanguage, setCurrentLanguage] = useState('Template');
	const [disabledTemplateForm, setDisabledTemplateForm] = useState(false);
	const [allFormsValues, setAllFormsValues] = useState<any>(defaultTranslationsObject);

	useEffect(() => {
		if (surveyTemplate) {
			setAllFormsValues(defaultTranslationsObject);
		}
	}, [surveyTemplate]);

	const englishFormRef = useRef<SurveyTemplateFormRef>(null);
	const translateFormRef = useRef<SurveyTemplateFormRef>(null);
	const settingsFormRef = useRef<{ isDirty: boolean; isSubmitting: boolean; isSubmitted: boolean }>(null);

	const hasUnsavedChanges = () =>
		!!(
			(englishFormRef.current?.isDirty && !settingsFormRef.current?.isSubmitting) ||
			(translateFormRef.current?.isDirty && !settingsFormRef.current?.isSubmitting) ||
			(settingsFormRef.current?.isDirty && !settingsFormRef.current?.isSubmitting)
		);
	const blocker = useBlocker(hasUnsavedChanges);

	const { openModal: openModalConfirm, closeModal: closeModalConfirm } =
		useMuiModal<ConfirmationModalProps>(ConfirmationModal);

	useEffect(() => {
		if (blocker.state === 'blocked') {
			openModalConfirm({
				title: 'Unsaved changes',
				description: 'You have unsaved changes. Are you sure you want to leave this page?',
				onSubmit: () => {
					closeModalConfirm();
					blocker.proceed();
				},
				onClose: () => {
					closeModalConfirm();
					blocker.reset();
				},
				modalWidth: 600,
				leftButtonProps: {
					children: t('engagements.forms.buttons.cancel'),
					variant: 'secondary',
					sx: buttonModalWindowStyles,
				},
				rightButtonProps: {
					children: t('engagements.forms.buttons.confirm'),
					contraModeStyleButton: true,
					sx: buttonModalWindowStyles,
				},
			});
		}
	}, [blocker.state]);

	return (
		<Container maxWidth={false} disableGutters sx={{ display: 'flex', height: '100%', gap: 1, p: 0 }}>
			<Paper elevation={0} sx={{ width: '58%', borderRadius: '12px', height: '100%', p: '20px' }}>
				{currentLanguage === 'Template' ? (
					<EnglishSurveyTemplateForm
						currentLanguage={currentLanguage}
						defaultValues={allFormsValues.Template}
						setAllFormsValues={setAllFormsValues}
						setDisabledTemplateForm={setDisabledTemplateForm}
						ref={englishFormRef}
					/>
				) : (
					<TranslateSurveyTemplateForm
						currentLanguage={currentLanguage}
						defaultValues={allFormsValues[currentLanguage]}
						setAllFormsValues={setAllFormsValues}
						ref={translateFormRef}
					/>
				)}
			</Paper>
			<Paper elevation={0} sx={{ width: '42%', borderRadius: '12px', height: '100%', p: '20px' }}>
				<SurveySettingsForm
					currentLanguage={currentLanguage}
					defaultValues={initialSettingsValues}
					allFormsValues={allFormsValues}
					setAllFormsValues={setAllFormsValues}
					setCurrentLanguage={setCurrentLanguage}
					disableSubmitEnglishTemplate={disabledTemplateForm}
					englishTemplateFormRef={englishFormRef}
					translateTemplateFormRef={translateFormRef}
					ref={settingsFormRef}
				/>
			</Paper>
		</Container>
	);
};
